
import * as http from "http";
import * as url from "url";
import * as path from "path";
import * as fs from "fs";
import * as os from "os";
import { makeAssetHttpHandler } from "./assets/serveClient.js";

export type WebClientServerConfig = {
  host: string,
  port: number,
  /** Interface the HTTP server listens on; unset = every interface (see `GottiServerConfig.bindHost`). */
  bindHost?: string,
  publicPath: string,
  noWebsite?: boolean,
  clientFilePath?: string,
  clientFileName?: string,
  processId?: string,
  /**
   * Filesystem root for the Unified Assets API (mirrors `servers.assets.path`).
   * Its `client/` subtree is served over `GET /assets/client/<rel>` (`server/`
   * and traversal always 404 — see `makeAssetHttpHandler`). Defaults to
   * `<publicPath>/../assets` when unset.
   */
  assetsPath?: string,
}

const contentTypesByExtension: { [key: string]: string } = {
  '.html': 'text/html',
  '.js': 'text/javascript',
  '.css': 'text/css',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.ico': 'image/x-icon',
  '.json': 'application/json',
  '.pdf': 'application/pdf',
  '.mp3': 'audio/mpeg',
  '.wav': 'audio/wav',
  '.ogg': 'audio/ogg',
  '.aac': 'audio/aac',
  '.flac': 'audio/flac',
  // Add more as needed
};

import { getLogger } from "@gotti/core";

export class WebClientServer {
    private clientFilePath: string;
    private clientFileContent: any;
    readonly logger = getLogger('WebClientServer:', { kind: 'webClient' });
    private server : any;
    constructor(opts: WebClientServerConfig) {
      const { port, clientFilePath, clientFileName, noWebsite } = opts;
      const publicPath = opts.publicPath.startsWith('~') ? opts.publicPath.replace('~', os.homedir()) : opts.publicPath;
      const assetsRoot = opts.assetsPath || path.join(publicPath, '..', 'assets');
      // Invariant: assetsRoot must be disjoint from publicPath. The generic
      // static-file branch below (the request handler's final `else`) serves
      // ANY file under publicPath with no guard of its own — only the
      // `/assets/client/` route is guarded (via makeAssetHttpHandler, which
      // itself always 404s `server/` + traversal). If assetsRoot were inside
      // (or equal to) publicPath, `server/`-namespace assets (secrets, save
      // data, etc.) would become reachable over http through that generic
      // branch. The default layout (`<publicPath>/../assets`) is disjoint and
      // safe; enforce the same for any operator-supplied `assetsPath`.
      const resolvedPublicPath = path.resolve(publicPath);
      const resolvedAssetsRoot = path.resolve(assetsRoot);
      if (resolvedAssetsRoot === resolvedPublicPath || resolvedAssetsRoot.startsWith(resolvedPublicPath + path.sep)) {
        throw new Error(`gotti assets root (${assetsRoot}) must not be inside the web publicPath (${publicPath}) — server/ assets would be served over http. Use a disjoint directory (default: <publicPath>/../assets).`);
      }
      const assetHandler = makeAssetHttpHandler(assetsRoot);
      if(!noWebsite) {
       const currentDir = typeof __dirname !== 'undefined'
          ? __dirname 
          : path.dirname(new URL(import.meta.url).pathname);

        const fileName = clientFileName ? clientFileName : "index.html";
        const tryFilePaths = []
        if(clientFilePath) {
          tryFilePaths.push(clientFilePath);
          tryFilePaths.push(path.join(publicPath, clientFilePath));
          tryFilePaths.push(path.join(currentDir, clientFilePath));
          tryFilePaths.push(path.join(process.cwd(), clientFilePath));
        }
        tryFilePaths.push(path.join(publicPath, fileName));
        tryFilePaths.push(path.join(currentDir, publicPath, fileName));
        tryFilePaths.push(path.join(currentDir, fileName));
        tryFilePaths.push(path.join(process.cwd(), fileName));
        tryFilePaths.push(path.join(process.cwd(), publicPath, fileName));

        this.clientFilePath = tryFilePaths.shift();
        while(this.clientFilePath && !fs.existsSync(this.clientFilePath)) {
          this.clientFilePath = tryFilePaths.shift();
        }
       if(!fs.existsSync(this.clientFilePath)) {
        console.error(tryFilePaths.join("\n"));
        throw new Error(`No file found at any possible paths for a client file. Ensure it exists before starting web server. Or if you dont have a site for the client make sure to pass in the noWebsite=true option to WebClientServerConfig.`);
       }
    //    this.logger.log(`Found client file to serve at path: ${this.clientFilePath}`);

        this.clientFileContent = fs.readFileSync(this.clientFilePath, "binary");
      }
    

      this.server = http.createServer((request, response) => {
        const parsedUrl = url.parse(request.url || "", true);
        const pathname = parsedUrl.pathname ? parsedUrl.pathname : "/";

        if(pathname === "/") {
          if(!this.clientFileContent) {
            response.writeHead(404);
            response.end("No client file found");
            return;
          }
          response.writeHead(200, { 'Content-Type': 'text/html' });
          response.end(this.clientFileContent);
        } else if (pathname.startsWith("/assets/client/")) {
          // Unified Assets API: only the `client/` subtree is ever reachable
          // here (server/ + traversal 404 inside the handler itself).
          const rel = pathname.slice("/assets/".length);
          assetHandler(rel).then((result) => {
            if (result.status === 200 && result.bytes) {
              response.writeHead(200, { 'Content-Type': result.mimeType || 'application/octet-stream' });
              response.end(Buffer.from(result.bytes));
            } else {
              response.writeHead(404);
              response.end("File not found!");
            }
          }).catch((err) => {
            console.error(err);
            response.writeHead(404);
            response.end("File not found!");
          });
        } else {
          const contentType = contentTypesByExtension[path.extname(pathname).toLowerCase()] || 'text/plain';
          const filePath = path.join(publicPath, pathname.slice(1));
          fs.readFile(filePath, (err, data) => {
            if (err) {
              console.error(err);
              response.writeHead(404);
              response.end("File not found!");
            } else {
              response.writeHead(200, { 'Content-Type': contentType });
              response.end(data);
            }
          });
        }
      });
      // Same rule as the gate's: `bindHost` when set, else every interface (unchanged).
      const onListening = () => {
         this.logger.info(`Web client server is listening on ${opts.bindHost ?? '*'}:${port}`);
      };
      if (opts.bindHost) {
        this.server.listen(port, opts.bindHost, onListening);
      } else {
        this.server.listen(port, onListening);
      }
    }
    

    public refreshClientFile() {
      this.clientFileContent = fs.readFileSync(this.clientFilePath, "binary");
    }

    public close() {
      try {
        this.server.close();
      } catch (err) {
        this.logger.error(`Error closing WebClientServer: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  }